import { QueryClient } from "@tanstack/react-query";
import debounce from "lodash/debounce";

import { setPartQueryData } from "#modules/parts/infra/part.query";
import { setQuoteRequestLineQueryData } from "#modules/quoteRequestLines/infra/quoteRequestLine.query";
import { quoteRequestLinesBulkQuery } from "#modules/quoteRequestLines/infra/quoteRequestLinesBulk.query";

const DEBOUNCE_MS = 500;
const MAX_WAIT_MS = 2000;

const pendingByLineId = new Map<string, QueryClient>();

const flushPendingInvalidations = debounce(
  () => {
    const entries = [...pendingByLineId.entries()];
    pendingByLineId.clear();

    if (entries.length === 0) {
      return;
    }

    const queryClient = entries[0]![1];
    const ids = entries.map(([id]) => id);

    void flushBulk({ queryClient, ids });
  },
  DEBOUNCE_MS,
  { maxWait: MAX_WAIT_MS },
);

const flushBulk = async ({ queryClient, ids }: { queryClient: QueryClient; ids: string[] }) => {
  const lines = await queryClient.fetchQuery({
    ...quoteRequestLinesBulkQuery({ ids }),
    staleTime: 0,
  });

  lines.forEach((line) => {
    setQuoteRequestLineQueryData(queryClient, line.id, line);
    setPartQueryData(queryClient, line.partId, line.part);
  });
};

export const invalidateQuoteRequestLinesDebounced = ({
  queryClient,
  quoteRequestLineId,
}: {
  queryClient: QueryClient;
  quoteRequestLineId: string;
}) => {
  pendingByLineId.set(quoteRequestLineId, queryClient);
  flushPendingInvalidations();
};

export const resetInvalidateQuoteRequestLinesDebounced = () => {
  flushPendingInvalidations.cancel();
  pendingByLineId.clear();
};
